import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import CourierCostsTab from "@/components/suppliers/CourierCostsTab";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Building2,
  Plus,
  Search,
  Trash2,
  Phone,
  Mail,
  Globe,
  MapPin,
  CreditCard,
  FileText,
  Truck,
  RefreshCw,
  User,
  Building,
  Banknote,
  Clock,
  Package,
  DollarSign,
  Receipt,
  AlertTriangle,
  Check,
  CircleDollarSign,
  Loader2,
  Copy,
  History,
} from "lucide-react";
import { toast } from "sonner";
import SupplierDetailModal from "@/components/suppliers/SupplierDetailModal";
import SupplierDataCleanup from "@/components/suppliers/SupplierDataCleanup";
import DuplicateSuppliersDialog from "@/components/suppliers/DuplicateSuppliersDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PAYMENT_TERMS = ["CASH", "COD", "7 Days", "14 Days", "30 Days", "60 Days", "90 Days"];
const BANK_ACCOUNT_TYPES = ["Cheque", "Savings", "Transmission", "Current"];

const emptySupplier = {
  name: "",
  contact_person: "",
  phone: "",
  alt_phone: "",
  email: "",
  website: "",
  address: "",
  city: "",
  province: "",
  postal_code: "",
  vat_number: "",
  registration_number: "",
  account_number: "",  // Your account number with this supplier
  bank_name: "",
  bank_account_number: "",
  bank_branch_code: "",
  bank_account_type: "",
  payment_terms: "30 Days",
  is_cod: false,
  is_cash: false,
  is_consignment: false,
  credit_limit: 0,
  auto_email_orders: false,
  notes: "",
};

export const Suppliers = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [formData, setFormData] = useState(emptySupplier);
  const [saving, setSaving] = useState(false);
  
  // NEW: Detail modal state (Profile, Transactions, Journal, Reconcile)
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showCleanupModal, setShowCleanupModal] = useState(false);
  const [showDuplicatesModal, setShowDuplicatesModal] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  
  // OLD History modal states (keeping for backward compatibility)
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historySupplier, setHistorySupplier] = useState(null);
  const [supplierStats, setSupplierStats] = useState(null);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [grvHistory, setGrvHistory] = useState([]);
  const [historyTab, setHistoryTab] = useState("stats");
  
  // Financial data
  const [financialData, setFinancialData] = useState(null);
  const [supplierPayments, setSupplierPayments] = useState([]);
  const [unallocatedFunds, setUnallocatedFunds] = useState(0);
  const [availableToPay, setAvailableToPay] = useState(0);
  
  // Payment modal
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showOverpayConfirm, setShowOverpayConfirm] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    payment_date: new Date().toISOString().split('T')[0],
    amount: "",
    payment_method: "EFT",
    reference: "",
    notes: "",
    allocations: [],
    auto_allocate: false
  });
  const [savingPayment, setSavingPayment] = useState(false);
  
  // Manual allocation modal
  const [showAllocateModal, setShowAllocateModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [allocateAmount, setAllocateAmount] = useState("");
  const [savingAllocation, setSavingAllocation] = useState(false);
  
  // Ledger/Transactions data
  const [ledgerData, setLedgerData] = useState(null);
  const [loadingLedger, setLoadingLedger] = useState(false);
  
  // Journal Entry modal
  const [showJournalModal, setShowJournalModal] = useState(false);
  const [journalForm, setJournalForm] = useState({
    journal_type: "PAYMENT",
    date: new Date().toISOString().split('T')[0],
    amount: "",
    description: "",
    reference: "",
    payment_method: "EFT"
  });
  const [savingJournal, setSavingJournal] = useState(false);

  // Payment Reconciliation Email
  const [sendingPaymentRecon, setSendingPaymentRecon] = useState(false);

  const handleEmailPaymentRecon = async (supplier) => {
    if (!supplier) return;
    
    // Check if supplier has email
    if (!supplier.email) {
      // Prompt for email
      const email = prompt("This supplier has no email on file. Enter email address to send report:");
      if (!email) return;
      
      setSendingPaymentRecon(true);
      try {
        const threeMonthsAgo = new Date();
        threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
        const startDate = threeMonthsAgo.toISOString().split('T')[0];
        const endDate = new Date().toISOString().split('T')[0];
        
        await axios.post(`${API}/suppliers/${supplier.id}/email-payment-reconciliation`, {
          email_to: email,
          start_date: startDate,
          end_date: endDate
        });
        toast.success(`Payment reconciliation report sent to ${email}`);
      } catch (error) {
        toast.error(error.response?.data?.detail || "Failed to send report");
      } finally {
        setSendingPaymentRecon(false);
      }
      return;
    }
    
    // Supplier has email - confirm and send
    if (!confirm(`Send payment reconciliation report to ${supplier.email}?`)) return;
    
    setSendingPaymentRecon(true);
    try {
      const threeMonthsAgo = new Date();
      threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
      const startDate = threeMonthsAgo.toISOString().split('T')[0];
      const endDate = new Date().toISOString().split('T')[0];
      
      const res = await axios.post(`${API}/suppliers/${supplier.id}/email-payment-reconciliation`, {
        start_date: startDate,
        end_date: endDate
      });
      toast.success(`Report sent to ${supplier.email} (${res.data.payments_count} payments, R${res.data.total_amount?.toFixed(2)})`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send report");
    } finally {
      setSendingPaymentRecon(false);
    }
  };

  useEffect(() => {
    fetchSuppliers();
  }, [showInactive]);

  // Derive filtered list from state — no extra state, no race window
  // where a background refetch can briefly clobber the user's search.
  const filteredSuppliers = useMemo(() => {
    if (!searchQuery.trim()) return suppliers;
    const query = searchQuery.toLowerCase();
    return suppliers.filter(
      (s) =>
        s.name?.toLowerCase().includes(query) ||
        s.supplier_number?.toLowerCase().includes(query) ||
        s.contact_person?.toLowerCase().includes(query) ||
        s.phone?.includes(query) ||
        s.email?.toLowerCase().includes(query)
    );
  }, [searchQuery, suppliers]);

  const fetchSuppliers = async (preserveScroll = false) => {
    // Store scroll position if we want to preserve it
    const scrollY = preserveScroll ? window.scrollY : 0;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/suppliers?active_only=${!showInactive}&include_balance=true`);
      // Sort suppliers alphabetically by name
      const sortedSuppliers = (response.data || []).sort((a, b) => 
        (a.name || '').localeCompare(b.name || '')
      );
      setSuppliers(sortedSuppliers);
      
      // Restore scroll position after state update
      if (preserveScroll) {
        setTimeout(() => window.scrollTo(0, scrollY), 50);
      }
    } catch (error) {
      console.error("Error fetching suppliers:", error);
      toast.error("Failed to load suppliers");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenNew = () => {
    setEditingSupplier(null);
    setFormData(emptySupplier);
    setShowSupplierModal(true);
  };

  const handleEdit = (supplier) => {
    setEditingSupplier(supplier);
    setFormData({
      name: supplier.name || "",
      contact_person: supplier.contact_person || "",
      phone: supplier.phone || "",
      alt_phone: supplier.alt_phone || "",
      email: supplier.email || "",
      website: supplier.website || "",
      address: supplier.address || "",
      city: supplier.city || "",
      province: supplier.province || "",
      postal_code: supplier.postal_code || "",
      vat_number: supplier.vat_number || "",
      registration_number: supplier.registration_number || "",
      account_number: supplier.account_number || "",
      bank_name: supplier.bank_name || "",
      bank_account_number: supplier.bank_account_number || "",
      bank_branch_code: supplier.bank_branch_code || "",
      bank_account_type: supplier.bank_account_type || "",
      payment_terms: supplier.payment_terms || "30 Days",
      is_cod: supplier.is_cod || false,
      is_cash: supplier.is_cash || false,
      is_consignment: supplier.is_consignment || false,
      credit_limit: supplier.credit_limit || 0,
      auto_email_orders: supplier.auto_email_orders || false,
      notes: supplier.notes || "",
    });
    setShowSupplierModal(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error("Supplier name is required");
      return;
    }

    setSaving(true);
    try {
      // Belt-and-braces normalisation: force is_cod to match payment_terms so
      // any stale boolean from old data gets cleaned up on every save.
      const payload = {
        ...formData,
        is_cod: formData.payment_terms === "COD",
      };
      if (editingSupplier) {
        await axios.put(`${API}/suppliers/${editingSupplier.id}`, payload);
        toast.success("Supplier updated successfully");
      } else {
        await axios.post(`${API}/suppliers`, payload);
        toast.success("Supplier created successfully");
      }
      setShowSupplierModal(false);
      fetchSuppliers(true);  // Preserve scroll position
    } catch (error) {
      console.error("Error saving supplier:", error);
      toast.error(error.response?.data?.detail || "Failed to save supplier");
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (supplier) => {
    if (!window.confirm(`Are you sure you want to deactivate "${supplier.name}"?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/suppliers/${supplier.id}`);
      toast.success("Supplier deactivated");
      fetchSuppliers(true);  // Preserve scroll position
    } catch (error) {
      console.error("Error deactivating supplier:", error);
      toast.error("Failed to deactivate supplier");
    }
  };

  const handleReactivate = async (supplier) => {
    try {
      await axios.post(`${API}/suppliers/${supplier.id}/reactivate`);
      toast.success("Supplier reactivated");
      fetchSuppliers(true);  // Preserve scroll position
    } catch (error) {
      console.error("Error reactivating supplier:", error);
      toast.error("Failed to reactivate supplier");
    }
  };

  // Open the detail modal when clicking on a supplier row
  const handleSupplierClick = (supplier) => {
    setSelectedSupplier(supplier);
    setShowDetailModal(true);
  };

  const handleViewHistory = async (supplier) => {
    setHistorySupplier(supplier);
    setHistoryTab("stats");
    setShowHistoryModal(true);
    setFinancialData(null);
    setSupplierPayments([]);
    setUnallocatedFunds(0);
    setLedgerData(null);
    
    // Fetch all history data including financial
    try {
      const [statsRes, posRes, grvsRes, financialRes, paymentsRes, unallocRes] = await Promise.all([
        axios.get(`${API}/suppliers/${supplier.id}/stats`),
        axios.get(`${API}/suppliers/${supplier.id}/purchase-orders`),
        axios.get(`${API}/suppliers/${supplier.id}/grvs`),
        axios.get(`${API}/suppliers/${supplier.id}/financial`),
        axios.get(`${API}/suppliers/${supplier.id}/payments`),
        axios.get(`${API}/suppliers/${supplier.id}/unallocated-funds`)
      ]);
      
      setSupplierStats(statsRes.data);
      setPurchaseOrders(posRes.data.purchase_orders || []);
      setGrvHistory(grvsRes.data.grvs || []);
      setFinancialData(financialRes.data);
      setSupplierPayments(paymentsRes.data.payments || []);
      setUnallocatedFunds(unallocRes.data.unallocated_funds || 0);
    } catch (error) {
      console.error("Error fetching supplier history:", error);
      toast.error("Failed to load supplier history");
    }
  };

  const fetchLedger = async (supplierId) => {
    setLoadingLedger(true);
    try {
      const response = await axios.get(`${API}/suppliers/${supplierId}/transactions`);
      setLedgerData(response.data);
    } catch (error) {
      console.error("Error fetching ledger:", error);
      toast.error("Failed to load ledger");
    } finally {
      setLoadingLedger(false);
    }
  };

  const handleOpenJournalModal = () => {
    setJournalForm({
      journal_type: "PAYMENT",
      date: new Date().toISOString().split('T')[0],
      amount: "",
      description: "",
      reference: "",
      payment_method: "EFT"
    });
    setShowJournalModal(true);
  };

  const handleSaveJournal = async () => {
    if (!journalForm.amount || parseFloat(journalForm.amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    if (!journalForm.description.trim()) {
      toast.error("Please enter a description");
      return;
    }

    setSavingJournal(true);
    try {
      await axios.post(`${API}/suppliers/${historySupplier.id}/journal`, {
        ...journalForm,
        amount: parseFloat(journalForm.amount)
      });
      
      toast.success(`${journalForm.journal_type} entry saved successfully`);
      setShowJournalModal(false);
      
      // Refresh all data
      const [financialRes, paymentsRes, unallocRes, ledgerRes] = await Promise.all([
        axios.get(`${API}/suppliers/${historySupplier.id}/financial`),
        axios.get(`${API}/suppliers/${historySupplier.id}/payments`),
        axios.get(`${API}/suppliers/${historySupplier.id}/unallocated-funds`),
        axios.get(`${API}/suppliers/${historySupplier.id}/transactions`)
      ]);
      setFinancialData(financialRes.data);
      setSupplierPayments(paymentsRes.data.payments || []);
      setUnallocatedFunds(unallocRes.data.unallocated_funds || 0);
      setLedgerData(ledgerRes.data);
    } catch (error) {
      console.error("Error saving journal entry:", error);
      toast.error(error.response?.data?.detail || "Failed to save journal entry");
    } finally {
      setSavingJournal(false);
    }
  };

  const handleOpenPaymentModal = async () => {
    setPaymentForm({
      payment_date: new Date().toISOString().split('T')[0],
      amount: "",
      payment_method: "EFT",
      reference: "",
      notes: "",
      allocations: [],
      auto_allocate: false
    });
    
    // Fetch available to pay from supplier allocation
    try {
      const response = await axios.get(`${API}/supplier-allocation/supplier/${historySupplier.id}`);
      setAvailableToPay(response.data?.summary?.available_to_pay || 0);
    } catch (error) {
      console.error("Error fetching available to pay:", error);
      setAvailableToPay(0);
    }
    
    setShowPaymentModal(true);
  };

  const handleSavePayment = async (skipOverpayCheck = false) => {
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast.error("Please enter a valid payment amount");
      return;
    }
    
    const paymentAmount = parseFloat(paymentForm.amount);
    
    // Check if payment exceeds available to pay (unless confirmation was given)
    if (!skipOverpayCheck && paymentAmount > availableToPay && availableToPay > 0) {
      setShowOverpayConfirm(true);
      return;
    }

    setSavingPayment(true);
    try {
      const response = await axios.post(`${API}/suppliers/${historySupplier.id}/payments`, {
        ...paymentForm,
        amount: paymentAmount
      });
      
      // Show detailed message if auto-allocated
      if (paymentForm.auto_allocate && response.data.allocations?.length > 0) {
        const allocCount = response.data.allocations.length;
        toast.success(`Payment recorded and auto-allocated to ${allocCount} invoice(s)`);
      } else {
        toast.success("Payment recorded successfully");
      }
      
      setShowPaymentModal(false);
      setShowOverpayConfirm(false);
      
      // Refresh financial data
      const [financialRes, paymentsRes, unallocRes] = await Promise.all([
        axios.get(`${API}/suppliers/${historySupplier.id}/financial`),
        axios.get(`${API}/suppliers/${historySupplier.id}/payments`),
        axios.get(`${API}/suppliers/${historySupplier.id}/unallocated-funds`)
      ]);
      setFinancialData(financialRes.data);
      setSupplierPayments(paymentsRes.data.payments || []);
      setUnallocatedFunds(unallocRes.data.unallocated_funds || 0);
    } catch (error) {
      console.error("Error saving payment:", error);
      toast.error(error.response?.data?.detail || "Failed to save payment");
    } finally {
      setSavingPayment(false);
    }
  };
  
  // Manual allocation to a single invoice
  const handleOpenAllocateModal = (invoice) => {
    setSelectedInvoice(invoice);
    setAllocateAmount(Math.min(unallocatedFunds, invoice.outstanding).toFixed(2));
    setShowAllocateModal(true);
  };
  
  const handleSaveAllocation = async () => {
    if (!selectedInvoice || !allocateAmount || parseFloat(allocateAmount) <= 0) {
      toast.error("Please enter a valid allocation amount");
      return;
    }
    
    const amount = parseFloat(allocateAmount);
    if (amount > unallocatedFunds) {
      toast.error(`Amount exceeds unallocated funds (R ${unallocatedFunds.toFixed(2)})`);
      return;
    }
    if (amount > selectedInvoice.outstanding) {
      toast.error(`Amount exceeds invoice outstanding (R ${selectedInvoice.outstanding.toFixed(2)})`);
      return;
    }
    
    setSavingAllocation(true);
    try {
      const response = await axios.post(`${API}/suppliers/${historySupplier.id}/allocate-to-invoice`, {
        grv_id: selectedInvoice.grv_id,
        amount: amount
      });
      
      toast.success(`R ${response.data.amount_allocated.toFixed(2)} allocated to ${response.data.invoice_number}`);
      setShowAllocateModal(false);
      
      // Refresh data
      const [financialRes, paymentsRes, unallocRes] = await Promise.all([
        axios.get(`${API}/suppliers/${historySupplier.id}/financial`),
        axios.get(`${API}/suppliers/${historySupplier.id}/payments`),
        axios.get(`${API}/suppliers/${historySupplier.id}/unallocated-funds`)
      ]);
      setFinancialData(financialRes.data);
      setSupplierPayments(paymentsRes.data.payments || []);
      setUnallocatedFunds(unallocRes.data.unallocated_funds || 0);
    } catch (error) {
      console.error("Error allocating payment:", error);
      toast.error(error.response?.data?.detail || "Failed to allocate payment");
    } finally {
      setSavingAllocation(false);
    }
  };

  const handleAllocationChange = (invoiceIndex, amount) => {
    const invoice = financialData?.invoices?.[invoiceIndex];
    if (!invoice) return;
    
    const newAllocations = [...paymentForm.allocations];
    const existingIndex = newAllocations.findIndex(a => a.grv_id === invoice.grv_id);
    
    if (existingIndex >= 0) {
      if (amount > 0) {
        newAllocations[existingIndex].amount = parseFloat(amount);
      } else {
        newAllocations.splice(existingIndex, 1);
      }
    } else if (amount > 0) {
      newAllocations.push({
        grv_id: invoice.grv_id,
        invoice_number: invoice.invoice_number,
        amount: parseFloat(amount)
      });
    }
    
    setPaymentForm(prev => ({ ...prev, allocations: newAllocations }));
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Sticky Header */}
      <div className="sticky top-[72px] z-10 bg-white pb-4 -mx-6 px-6 pt-4 border-b border-gray-100 shadow-sm">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-3">
            <Building2 className="h-8 w-8 text-blue-600" />
            <div>
              <h1 className="text-3xl font-bold">Suppliers</h1>
              <p className="text-gray-500">Manage your supplier database & courier costs</p>
            </div>
          </div>
        </div>
      </div>

      <div className="h-4"></div>

      <Tabs defaultValue="list" className="w-full">
        <TabsList className="mb-4" data-testid="suppliers-page-tabs">
          <TabsTrigger value="list" data-testid="tab-suppliers-list">
            <Building2 className="h-4 w-4 mr-1" /> Suppliers List
          </TabsTrigger>
          <TabsTrigger value="courier" data-testid="tab-courier-costs">
            <Truck className="h-4 w-4 mr-1" /> Courier Costs
          </TabsTrigger>
        </TabsList>

        <TabsContent value="list">
          {/* Action toolbar */}
          <div className="flex justify-end gap-2 mb-3">
            <Button
              variant="outline"
              onClick={() => setShowDuplicatesModal(true)}
              className="text-red-600 border-red-300 hover:bg-red-50"
              data-testid="open-find-duplicates-btn"
            >
              <Copy className="h-4 w-4 mr-2" />
              Find Duplicates
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowCleanupModal(true)}
              className="text-orange-600 border-orange-300 hover:bg-orange-50"
            >
              <AlertTriangle className="h-4 w-4 mr-2" />
              Data Cleanup
            </Button>
            <Button onClick={handleOpenNew} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="h-4 w-4 mr-2" />
              Add Supplier
            </Button>
          </div>

          {/* Search and Filters */}
          <Card className="shadow-none border-gray-200 mb-4">
            <CardContent className="py-3">
              <div className="flex gap-4 items-center">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by name, number, contact, phone, or email..."
                    className="pl-10"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="showInactive"
                    checked={showInactive}
                    onCheckedChange={setShowInactive}
                  />
                  <Label htmlFor="showInactive" className="text-sm">Show inactive</Label>
                </div>
                <Button variant="outline" onClick={fetchSuppliers}>
                  <RefreshCw className="h-4 w-4 mr-1" />
                  Refresh
                </Button>
              </div>
            </CardContent>
          </Card>

      {/* Suppliers Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="w-[100px]">Number</TableHead>
                <TableHead>Supplier Name</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Payment Terms</TableHead>
                <TableHead className="text-right">Credit Limit</TableHead>
                <TableHead className="text-right">Balance</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8">
                    Loading suppliers...
                  </TableCell>
                </TableRow>
              ) : filteredSuppliers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-gray-500">
                    <Building2 className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No suppliers found</p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredSuppliers.map((supplier) => (
                  <TableRow 
                    key={supplier.id} 
                    className={`cursor-pointer ${!supplier.is_active ? "bg-gray-50 opacity-60" : "hover:bg-blue-50"}`}
                    onClick={() => handleSupplierClick(supplier)}
                  >
                    <TableCell className="font-mono font-semibold">
                      {supplier.supplier_number}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{supplier.name}</div>
                      {supplier.city && (
                        <div className="text-xs text-gray-500">{supplier.city}</div>
                      )}
                    </TableCell>
                    <TableCell>
                      {supplier.contact_person || "-"}
                    </TableCell>
                    <TableCell>
                      {supplier.phone || "-"}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 flex-wrap">
                        {supplier.payment_terms}
                        {supplier.is_cod && supplier.payment_terms === "COD" && (
                          <Badge variant="secondary" className="text-xs">COD</Badge>
                        )}
                        {supplier.is_cash && (
                          <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-300">CASH</Badge>
                        )}
                        {supplier.is_consignment && (
                          <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-300">Consignment</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      {supplier.credit_limit > 0 ? `R ${supplier.credit_limit.toFixed(2)}` : "-"}
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={`font-semibold ${
                        (supplier.balance || 0) > 0 
                          ? 'text-red-600' 
                          : 'text-green-600'
                      }`}>
                        R {(supplier.balance || 0).toFixed(2)}
                      </span>
                    </TableCell>
                    <TableCell>
                      {supplier.is_active ? (
                        <Badge className="bg-green-100 text-green-700">Active</Badge>
                      ) : (
                        <Badge variant="secondary">Inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                        {supplier.is_active ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeactivate(supplier)}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            title="Deactivate"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleReactivate(supplier)}
                            className="text-green-500 hover:text-green-700 hover:bg-green-50"
                            title="Reactivate"
                          >
                            <RefreshCw className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Summary */}
      <div className="mt-4 text-sm text-gray-500">
        Showing {filteredSuppliers.length} of {suppliers.length} suppliers
      </div>
        </TabsContent>

        <TabsContent value="courier">
          <CourierCostsTab />
        </TabsContent>
      </Tabs>

      {/* Supplier Form Modal */}
      <Dialog open={showSupplierModal} onOpenChange={setShowSupplierModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              {editingSupplier ? `Edit Supplier: ${editingSupplier.supplier_number}` : "Add New Supplier"}
            </DialogTitle>
          </DialogHeader>

          <Tabs defaultValue="basic" className="mt-4">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="basic">Basic Info</TabsTrigger>
              <TabsTrigger value="contact">Contact</TabsTrigger>
              <TabsTrigger value="banking">Banking</TabsTrigger>
              <TabsTrigger value="terms">Terms & Notes</TabsTrigger>
            </TabsList>

            {/* Basic Info Tab */}
            <TabsContent value="basic" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Supplier Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    placeholder="Enter supplier name"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Contact Person</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <Input
                      value={formData.contact_person}
                      onChange={(e) => handleChange("contact_person", e.target.value)}
                      placeholder="Primary contact name"
                      className="pl-9"
                    />
                  </div>
                </div>
                <div>
                  <Label>VAT Number</Label>
                  <Input
                    value={formData.vat_number}
                    onChange={(e) => handleChange("vat_number", e.target.value)}
                    placeholder="VAT registration number"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Company Registration</Label>
                  <div className="relative mt-1">
                    <Building className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <Input
                      value={formData.registration_number}
                      onChange={(e) => handleChange("registration_number", e.target.value)}
                      placeholder="Registration number"
                      className="pl-9"
                    />
                  </div>
                </div>
                <div>
                  <Label>Your Account Number</Label>
                  <Input
                    value={formData.account_number}
                    onChange={(e) => handleChange("account_number", e.target.value)}
                    placeholder="Your account # with this supplier"
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">The account number they assigned to you</p>
                </div>
              </div>
            </TabsContent>

            {/* Contact Tab */}
            <TabsContent value="contact" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Phone</Label>
                  <div className="relative mt-1">
                    <Phone className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <Input
                      value={formData.phone}
                      onChange={(e) => handleChange("phone", e.target.value)}
                      placeholder="Primary phone"
                      className="pl-9"
                    />
                  </div>
                </div>
                <div>
                  <Label>Alternative Phone</Label>
                  <div className="relative mt-1">
                    <Phone className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <Input
                      value={formData.alt_phone}
                      onChange={(e) => handleChange("alt_phone", e.target.value)}
                      placeholder="Alternative phone"
                      className="pl-9"
                    />
                  </div>
                </div>
                <div>
                  <Label>Email</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange("email", e.target.value)}
                      placeholder="email@supplier.com"
                      className="pl-9"
                    />
                  </div>
                  {formData.email && (
                    <div className="flex items-center space-x-2 mt-2">
                      <Checkbox
                        id="auto_email_orders"
                        checked={formData.auto_email_orders}
                        onCheckedChange={(checked) => handleChange("auto_email_orders", checked)}
                      />
                      <Label htmlFor="auto_email_orders" className="text-sm font-normal cursor-pointer">
                        Auto-email purchase orders when finalized
                      </Label>
                    </div>
                  )}
                </div>
                <div>
                  <Label>Website</Label>
                  <div className="relative mt-1">
                    <Globe className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <Input
                      value={formData.website}
                      onChange={(e) => handleChange("website", e.target.value)}
                      placeholder="www.supplier.com"
                      className="pl-9"
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-4 mt-4">
                <Label className="text-base font-semibold flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Address
                </Label>
                <div className="grid grid-cols-2 gap-4 mt-3">
                  <div className="col-span-2">
                    <Label>Street Address</Label>
                    <Input
                      value={formData.address}
                      onChange={(e) => handleChange("address", e.target.value)}
                      placeholder="Street address"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>City</Label>
                    <Input
                      value={formData.city}
                      onChange={(e) => handleChange("city", e.target.value)}
                      placeholder="City"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Province</Label>
                    <Input
                      value={formData.province}
                      onChange={(e) => handleChange("province", e.target.value)}
                      placeholder="Province"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Postal Code</Label>
                    <Input
                      value={formData.postal_code}
                      onChange={(e) => handleChange("postal_code", e.target.value)}
                      placeholder="Postal code"
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Banking Tab */}
            <TabsContent value="banking" className="space-y-4 mt-4">
              <div className="flex items-center gap-2 mb-4">
                <Banknote className="h-5 w-5 text-gray-500" />
                <span className="font-semibold">Banking Details (for EFT payments)</span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Bank Name</Label>
                  <Input
                    value={formData.bank_name}
                    onChange={(e) => handleChange("bank_name", e.target.value)}
                    placeholder="e.g., FNB, ABSA, Standard Bank"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Account Type</Label>
                  <Select 
                    value={formData.bank_account_type} 
                    onValueChange={(v) => handleChange("bank_account_type", v)}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select account type" />
                    </SelectTrigger>
                    <SelectContent>
                      {[...BANK_ACCOUNT_TYPES].sort().map(type => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Account Number</Label>
                  <Input
                    value={formData.bank_account_number}
                    onChange={(e) => handleChange("bank_account_number", e.target.value)}
                    placeholder="Bank account number"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Branch Code</Label>
                  <Input
                    value={formData.bank_branch_code}
                    onChange={(e) => handleChange("bank_branch_code", e.target.value)}
                    placeholder="Branch code"
                    className="mt-1"
                  />
                </div>
              </div>
            </TabsContent>

            {/* Terms & Notes Tab */}
            <TabsContent value="terms" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Payment Terms</Label>
                  <Select 
                    value={formData.payment_terms} 
                    onValueChange={(v) => {
                      // Auto-sync the COD checkbox to the selected term so the
                      // list view never disagrees with the form: if the user
                      // picks anything other than "COD" we clear is_cod; if
                      // they pick "COD" we set it.
                      setFormData(prev => ({
                        ...prev,
                        payment_terms: v,
                        is_cod: v === "COD",
                      }));
                    }}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[...PAYMENT_TERMS].sort().map(term => (
                        <SelectItem key={term} value={term}>{term}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Credit Limit (R)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.credit_limit || ""}
                    onChange={(e) => handleChange("credit_limit", parseFloat(e.target.value) || 0)}
                    placeholder="0.00"
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="flex items-center space-x-2 mt-4">
                <Checkbox
                  id="is_cod"
                  checked={formData.is_cod}
                  onCheckedChange={(checked) => handleChange("is_cod", checked)}
                />
                <Label htmlFor="is_cod" className="flex items-center gap-2">
                  COD Supplier
                  <span className="text-xs text-gray-500">(Paid by EFT on delivery)</span>
                </Label>
              </div>

              <div className="flex items-center space-x-2 mt-3">
                <Checkbox
                  id="is_cash"
                  checked={formData.is_cash}
                  onCheckedChange={(checked) => handleChange("is_cash", checked)}
                  data-testid="supplier-is-cash-checkbox"
                />
                <Label htmlFor="is_cash" className="flex items-center gap-2">
                  Cash Supplier
                  <span className="text-xs text-gray-500">(Paid in physical cash from till)</span>
                </Label>
              </div>

              <div className="flex items-center space-x-2 mt-3">
                <Checkbox
                  id="is_consignment"
                  checked={formData.is_consignment}
                  onCheckedChange={(checked) => handleChange("is_consignment", checked)}
                />
                <Label htmlFor="is_consignment" className="flex items-center gap-2">
                  Consignment Stock Supplier
                  <span className="text-xs text-gray-500">(Stock owned by supplier until sold)</span>
                </Label>
              </div>

              <div className="mt-4">
                <Label>Notes</Label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => handleChange("notes", e.target.value)}
                  placeholder="Additional notes about this supplier..."
                  className="w-full mt-1 border rounded-md p-3 min-h-[100px]"
                />
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowSupplierModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : editingSupplier ? "Update Supplier" : "Create Supplier"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Modal */}
      <Dialog open={showHistoryModal} onOpenChange={setShowHistoryModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Supplier History: {historySupplier?.name}
              <Badge variant="outline" className="ml-2">{historySupplier?.supplier_number}</Badge>
            </DialogTitle>
          </DialogHeader>

          <Tabs value={historyTab} onValueChange={(tab) => {
            setHistoryTab(tab);
            if (tab === "ledger" && !ledgerData) {
              fetchLedger(historySupplier?.id);
            }
          }} className="mt-4">
            <TabsList className="grid w-full grid-cols-6">
              <TabsTrigger value="stats">Overview</TabsTrigger>
              <TabsTrigger value="ledger">Ledger</TabsTrigger>
              <TabsTrigger value="financial">Financial</TabsTrigger>
              <TabsTrigger value="reconcile">Reconciliation</TabsTrigger>
              <TabsTrigger value="pos">Purchase Orders</TabsTrigger>
              <TabsTrigger value="grvs">GRV History</TabsTrigger>
            </TabsList>

            {/* Stats Tab */}
            <TabsContent value="stats" className="mt-4">
              {supplierStats ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="pt-4">
                      <div className="flex items-center gap-2 text-gray-500 text-sm">
                        <FileText className="h-4 w-4" />
                        Total POs
                      </div>
                      <div className="text-2xl font-bold mt-1">
                        {supplierStats.total_purchase_orders}
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <div className="flex items-center gap-2 text-gray-500 text-sm">
                        <Clock className="h-4 w-4" />
                        Pending POs
                      </div>
                      <div className="text-2xl font-bold mt-1 text-orange-600">
                        {supplierStats.pending_purchase_orders}
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <div className="flex items-center gap-2 text-gray-500 text-sm">
                        <Truck className="h-4 w-4" />
                        Total GRVs
                      </div>
                      <div className="text-2xl font-bold mt-1">
                        {supplierStats.total_grvs}
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <div className="flex items-center gap-2 text-gray-500 text-sm">
                        <CreditCard className="h-4 w-4" />
                        Total Purchased
                      </div>
                      <div className="text-2xl font-bold mt-1 text-green-600">
                        R {supplierStats.total_purchased_value?.toFixed(2)}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">Loading statistics...</div>
              )}
              
              {supplierStats?.last_grv_date && (
                <div className="mt-4 text-sm text-gray-500">
                  Last GRV received: {supplierStats.last_grv_date}
                </div>
              )}
            </TabsContent>

            {/* Ledger Tab - Full Transaction History */}
            <TabsContent value="ledger" className="mt-4">
              <div className="space-y-4">
                {/* Ledger Header with Actions */}
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-lg">Transaction Ledger</h3>
                    <p className="text-sm text-gray-500">Complete history of invoices, payments, and journal entries</p>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => fetchLedger(historySupplier?.id)}
                    >
                      <RefreshCw className="h-4 w-4 mr-1" />
                      Refresh
                    </Button>
                    <Button 
                      onClick={handleOpenJournalModal}
                      size="sm"
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Journal Entry
                    </Button>
                  </div>
                </div>

                {/* Summary Card */}
                {ledgerData && (
                  <Card className="bg-gray-50">
                    <CardContent className="py-3">
                      <div className="grid grid-cols-4 gap-4 text-center">
                        <div>
                          <div className="text-sm text-gray-500">Opening Balance</div>
                          <div className="text-lg font-semibold">
                            R {ledgerData.summary?.opening_balance?.toFixed(2) || "0.00"}
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-500">Total Debits</div>
                          <div className="text-lg font-semibold text-red-600">
                            R {ledgerData.summary?.total_debit?.toFixed(2) || "0.00"}
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-500">Total Credits</div>
                          <div className="text-lg font-semibold text-green-600">
                            R {ledgerData.summary?.total_credit?.toFixed(2) || "0.00"}
                          </div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-500">Balance</div>
                          <div className={`text-lg font-bold ${ledgerData.summary?.balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                            R {ledgerData.summary?.balance?.toFixed(2) || "0.00"}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Transactions Table */}
                <Card>
                  <CardContent className="p-0">
                    {loadingLedger ? (
                      <div className="text-center py-8 text-gray-500">Loading ledger...</div>
                    ) : !ledgerData ? (
                      <div className="text-center py-8 text-gray-500">
                        <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                        <p>Click the Ledger tab to load transactions</p>
                      </div>
                    ) : ledgerData.transactions?.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                        <p>No transactions recorded</p>
                      </div>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-gray-50">
                            <TableHead>Date</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead>Doc No</TableHead>
                            <TableHead>Description</TableHead>
                            <TableHead>Reference</TableHead>
                            <TableHead className="text-right">Debit</TableHead>
                            <TableHead className="text-right">Credit</TableHead>
                            <TableHead>User</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {ledgerData.transactions.map((txn, idx) => (
                            <TableRow 
                              key={idx}
                              className={
                                txn.doc_type === "OPENING" ? "bg-blue-50" :
                                txn.doc_type === "INVOICE" ? "bg-red-50/30" :
                                txn.doc_type === "PAYMENT" ? "bg-green-50/30" :
                                txn.doc_type === "CREDIT" || txn.doc_type === "DISCOUNT" ? "bg-amber-50/30" :
                                ""
                              }
                            >
                              <TableCell className="font-mono text-sm">{txn.date}</TableCell>
                              <TableCell>
                                <Badge 
                                  variant={
                                    txn.doc_type === "INVOICE" ? "destructive" :
                                    txn.doc_type === "PAYMENT" ? "default" :
                                    txn.doc_type === "CREDIT" || txn.doc_type === "DISCOUNT" ? "secondary" :
                                    "outline"
                                  }
                                  className="text-xs"
                                >
                                  {txn.doc_type}
                                </Badge>
                              </TableCell>
                              <TableCell className="font-mono text-sm">{txn.doc_no}</TableCell>
                              <TableCell className="max-w-xs truncate" title={txn.description}>
                                {txn.description}
                              </TableCell>
                              <TableCell className="text-sm text-gray-500">{txn.reference || "-"}</TableCell>
                              <TableCell className="text-right font-mono">
                                {txn.debit > 0 ? (
                                  <span className="text-red-600">R {txn.debit.toFixed(2)}</span>
                                ) : "-"}
                              </TableCell>
                              <TableCell className="text-right font-mono">
                                {txn.credit > 0 ? (
                                  <span className="text-green-600">R {txn.credit.toFixed(2)}</span>
                                ) : "-"}
                              </TableCell>
                              <TableCell className="text-sm text-gray-500">{txn.user}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Financial Tab - Aging Analysis */}
            <TabsContent value="financial" className="mt-4">
              {financialData ? (
                <div className="space-y-6">
                  {/* Aging Summary Table */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <DollarSign className="h-4 w-4" />
                        Aging Analysis - Amount Owed to Supplier
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-gray-50">
                            <TableHead className="text-center">Current</TableHead>
                            <TableHead className="text-center">30 Days</TableHead>
                            <TableHead className="text-center">60 Days</TableHead>
                            <TableHead className="text-center">90 Days</TableHead>
                            <TableHead className="text-center">120 Days</TableHead>
                            <TableHead className="text-center">120+ Days</TableHead>
                            <TableHead className="text-center font-bold">Balance</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          <TableRow>
                            <TableCell className="text-center">
                              R {financialData.aging.current.toFixed(2)}
                            </TableCell>
                            <TableCell className={`text-center ${financialData.aging.days_30 > 0 ? "text-yellow-600" : ""}`}>
                              R {financialData.aging.days_30.toFixed(2)}
                            </TableCell>
                            <TableCell className={`text-center ${financialData.aging.days_60 > 0 ? "text-orange-600" : ""}`}>
                              R {financialData.aging.days_60.toFixed(2)}
                            </TableCell>
                            <TableCell className={`text-center ${financialData.aging.days_90 > 0 ? "text-red-500" : ""}`}>
                              R {financialData.aging.days_90.toFixed(2)}
                            </TableCell>
                            <TableCell className={`text-center ${financialData.aging.days_120 > 0 ? "text-red-600" : ""}`}>
                              R {financialData.aging.days_120.toFixed(2)}
                            </TableCell>
                            <TableCell className={`text-center ${financialData.aging.over_120 > 0 ? "text-red-700 font-semibold" : ""}`}>
                              R {financialData.aging.over_120.toFixed(2)}
                            </TableCell>
                            <TableCell className="text-center font-bold text-lg">
                              R {financialData.aging.balance.toFixed(2)}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>

                  {/* Summary Cards */}
                  <div className="grid grid-cols-3 gap-4">
                    <Card>
                      <CardContent className="pt-4">
                        <div className="text-gray-500 text-sm">Total Invoiced</div>
                        <div className="text-xl font-bold mt-1">
                          R {financialData.summary.total_invoiced.toFixed(2)}
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <div className="text-gray-500 text-sm">Total Paid</div>
                        <div className="text-xl font-bold mt-1 text-green-600">
                          R {financialData.summary.total_paid.toFixed(2)}
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <div className="text-gray-500 text-sm">Outstanding</div>
                        <div className={`text-xl font-bold mt-1 ${financialData.summary.total_outstanding > 0 ? "text-red-600" : "text-green-600"}`}>
                          R {financialData.summary.total_outstanding.toFixed(2)}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Outstanding Invoices */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Outstanding Invoices</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Invoice #</TableHead>
                            <TableHead>Date</TableHead>
                            <TableHead>Due Date</TableHead>
                            <TableHead className="text-right">Invoice Total</TableHead>
                            <TableHead className="text-right">Paid</TableHead>
                            <TableHead className="text-right">Outstanding</TableHead>
                            <TableHead>Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {financialData.invoices.filter(inv => inv.outstanding > 0).length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={7} className="text-center py-4 text-gray-500">
                                <Check className="h-8 w-8 mx-auto mb-2 text-green-500" />
                                All invoices are paid
                              </TableCell>
                            </TableRow>
                          ) : (
                            financialData.invoices.filter(inv => inv.outstanding > 0).map((invoice, idx) => (
                              <TableRow key={idx} className={invoice.days_overdue > 30 ? "bg-red-50" : ""}>
                                <TableCell className="font-mono">{invoice.invoice_number}</TableCell>
                                <TableCell>{invoice.received_date}</TableCell>
                                <TableCell>{invoice.due_date}</TableCell>
                                <TableCell className="text-right">R {invoice.invoice_total.toFixed(2)}</TableCell>
                                <TableCell className="text-right text-green-600">R {invoice.amount_paid.toFixed(2)}</TableCell>
                                <TableCell className="text-right font-semibold">R {invoice.outstanding.toFixed(2)}</TableCell>
                                <TableCell>
                                  {invoice.status === "OVERDUE" ? (
                                    <Badge variant="destructive" className="flex items-center gap-1 w-fit">
                                      <AlertTriangle className="h-3 w-3" />
                                      {invoice.days_overdue} days
                                    </Badge>
                                  ) : (
                                    <Badge variant="secondary">Current</Badge>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>

                  {/* Record Payment Button */}
                  <div className="flex justify-end">
                    <Button onClick={handleOpenPaymentModal} className="bg-green-600 hover:bg-green-700">
                      <CircleDollarSign className="h-4 w-4 mr-2" />
                      Record Payment
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">Loading financial data...</div>
              )}
            </TabsContent>

            {/* Reconciliation Tab */}
            <TabsContent value="reconcile" className="mt-4">
              <div className="space-y-6">
                {/* Unallocated Funds Banner */}
                {unallocatedFunds > 0 && (
                  <Card className="bg-amber-50 border-amber-200">
                    <CardContent className="py-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="h-5 w-5 text-amber-600" />
                          <span className="font-medium text-amber-800">
                            Unallocated Funds: R {unallocatedFunds.toFixed(2)}
                          </span>
                        </div>
                        <span className="text-sm text-amber-700">
                          Click on an open invoice below to allocate
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {/* Payment History */}
                <Card>
                  <CardHeader className="pb-2 flex flex-row items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Receipt className="h-4 w-4" />
                      Payment History
                    </CardTitle>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleEmailPaymentRecon(selectedSupplier)}
                        disabled={sendingPaymentRecon}
                      >
                        {sendingPaymentRecon ? (
                          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        ) : (
                          <Mail className="h-4 w-4 mr-1" />
                        )}
                        Email Recon Report
                      </Button>
                      <Button onClick={handleOpenPaymentModal} size="sm">
                        <Plus className="h-4 w-4 mr-1" />
                        New Payment
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {supplierPayments.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <Banknote className="h-12 w-12 mx-auto mb-2 opacity-50" />
                        <p>No payments recorded</p>
                      </div>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Reference</TableHead>
                            <TableHead>Date</TableHead>
                            <TableHead>Method</TableHead>
                            <TableHead>Bank Ref</TableHead>
                            <TableHead className="text-right">Amount</TableHead>
                            <TableHead>Allocated To</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {supplierPayments.map((payment) => {
                            const totalAllocated = payment.allocations?.reduce((sum, a) => sum + a.amount, 0) || 0;
                            const unalloc = payment.amount - totalAllocated;
                            return (
                              <TableRow key={payment.id}>
                                <TableCell className="font-mono">{payment.payment_ref}</TableCell>
                                <TableCell>{payment.payment_date}</TableCell>
                                <TableCell>{payment.payment_method}</TableCell>
                                <TableCell>{payment.reference || "-"}</TableCell>
                                <TableCell className="text-right font-semibold text-green-600">
                                  R {payment.amount.toFixed(2)}
                                </TableCell>
                                <TableCell>
                                  {payment.allocations?.length > 0 ? (
                                    <div className="text-xs">
                                      {payment.allocations.map((a, i) => (
                                        <span key={i} className="mr-1">
                                          {a.invoice_number}: R{a.amount.toFixed(2)}
                                          {i < payment.allocations.length - 1 ? "," : ""}
                                        </span>
                                      ))}
                                      {unalloc > 0 && (
                                        <Badge variant="outline" className="ml-1 text-amber-600 border-amber-300">
                                          R{unalloc.toFixed(2)} unalloc
                                        </Badge>
                                      )}
                                    </div>
                                  ) : (
                                    <Badge variant="outline" className="text-amber-600 border-amber-300">
                                      R{payment.amount.toFixed(2)} unallocated
                                    </Badge>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    )}
                  </CardContent>
                </Card>

                {/* Invoice Allocation Summary */}
                {financialData && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        Invoice Payment Status
                        {unallocatedFunds > 0 && (
                          <span className="text-sm font-normal text-gray-500 ml-2">
                            (Click an open invoice to allocate funds)
                          </span>
                        )}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Invoice #</TableHead>
                            <TableHead>GRV #</TableHead>
                            <TableHead>Date</TableHead>
                            <TableHead className="text-right">Total</TableHead>
                            <TableHead className="text-right">Paid</TableHead>
                            <TableHead className="text-right">Balance</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="text-right">Action</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {financialData.invoices.map((invoice, idx) => (
                            <TableRow 
                              key={idx}
                              className={invoice.outstanding > 0 && unallocatedFunds > 0 ? "cursor-pointer hover:bg-blue-50" : ""}
                              onClick={() => {
                                if (invoice.outstanding > 0 && unallocatedFunds > 0) {
                                  handleOpenAllocateModal(invoice);
                                }
                              }}
                            >
                              <TableCell className="font-mono">{invoice.invoice_number}</TableCell>
                              <TableCell className="font-mono text-xs">{invoice.grv_number}</TableCell>
                              <TableCell>{invoice.received_date}</TableCell>
                              <TableCell className="text-right">R {invoice.invoice_total.toFixed(2)}</TableCell>
                              <TableCell className="text-right text-green-600">R {invoice.amount_paid.toFixed(2)}</TableCell>
                              <TableCell className="text-right font-semibold">R {invoice.outstanding.toFixed(2)}</TableCell>
                              <TableCell>
                                {invoice.status === "PAID" ? (
                                  <Badge className="bg-green-100 text-green-700">
                                    <Check className="h-3 w-3 mr-1" />
                                    Paid
                                  </Badge>
                                ) : invoice.status === "OVERDUE" ? (
                                  <Badge variant="destructive">Overdue</Badge>
                                ) : (
                                  <Badge variant="secondary">Open</Badge>
                                )}
                              </TableCell>
                              <TableCell className="text-right">
                                {invoice.outstanding > 0 && unallocatedFunds > 0 ? (
                                  <Button 
                                    variant="outline" 
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleOpenAllocateModal(invoice);
                                    }}
                                  >
                                    <CircleDollarSign className="h-3 w-3 mr-1" />
                                    Allocate
                                  </Button>
                                ) : invoice.status === "PAID" ? (
                                  <span className="text-gray-400 text-xs">-</span>
                                ) : (
                                  <span className="text-gray-400 text-xs">No funds</span>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>

            {/* Purchase Orders Tab */}
            <TabsContent value="pos" className="mt-4">
              {purchaseOrders.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Package className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No purchase orders found</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>PO Number</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {purchaseOrders.map((po) => (
                      <TableRow key={po.id}>
                        <TableCell className="font-mono">{po.po_number}</TableCell>
                        <TableCell>{new Date(po.created_at).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <Badge variant={
                            po.status === "RECEIVED" ? "default" :
                            po.status === "PENDING" ? "secondary" :
                            po.status === "CANCELLED" ? "destructive" : "outline"
                          }>
                            {po.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">R {po.total?.toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>

            {/* GRVs Tab */}
            <TabsContent value="grvs" className="mt-4">
              {grvHistory.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Truck className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No GRV history found</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>GRV Number</TableHead>
                      <TableHead>Date Received</TableHead>
                      <TableHead>Invoice #</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {grvHistory.map((grv) => (
                      <TableRow key={grv.id}>
                        <TableCell className="font-mono">{grv.grv_number}</TableCell>
                        <TableCell>{grv.received_date}</TableCell>
                        <TableCell>{grv.supplier_invoice_number}</TableCell>
                        <TableCell className="text-right">R {grv.total?.toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowHistoryModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CircleDollarSign className="h-5 w-5" />
              Record Payment to {historySupplier?.name}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Available to Pay Info Box */}
            {availableToPay > 0 && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-700 font-medium">Available to Pay (from sales)</p>
                  <p className="text-xs text-green-600">Based on cost of goods sold from this supplier</p>
                </div>
                <p className="text-2xl font-bold text-green-700">R {availableToPay.toFixed(2)}</p>
              </div>
            )}
            
            {/* Warning if entering amount over available */}
            {paymentForm.amount && parseFloat(paymentForm.amount) > availableToPay && availableToPay > 0 && (
              <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 flex items-center gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0" />
                <div>
                  <p className="text-sm text-amber-800 font-medium">Payment exceeds Available to Pay</p>
                  <p className="text-xs text-amber-700">
                    You are paying R {(parseFloat(paymentForm.amount) - availableToPay).toFixed(2)} more than the available amount.
                    This may result in overpayment to this supplier.
                  </p>
                </div>
              </div>
            )}

            {/* Payment Details */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Payment Date</Label>
                <Input
                  type="date"
                  value={paymentForm.payment_date}
                  onChange={(e) => setPaymentForm(prev => ({ ...prev, payment_date: e.target.value }))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Amount (R)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm(prev => ({ ...prev, amount: e.target.value }))}
                  placeholder="0.00"
                  className="mt-1"
                />
                {availableToPay > 0 && (
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    className="text-xs text-green-600 p-0 h-auto mt-1"
                    onClick={() => setPaymentForm(prev => ({ ...prev, amount: availableToPay.toFixed(2) }))}
                  >
                    Use available amount (R {availableToPay.toFixed(2)})
                  </Button>
                )}
              </div>
              <div>
                <Label>Payment Method</Label>
                <Select 
                  value={paymentForm.payment_method} 
                  onValueChange={(v) => setPaymentForm(prev => ({ ...prev, payment_method: v }))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="EFT">EFT</SelectItem>
                    <SelectItem value="Cash">Cash</SelectItem>
                    <SelectItem value="Cheque">Cheque</SelectItem>
                    <SelectItem value="Card">Card</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Bank Reference / Cheque #</Label>
                <Input
                  value={paymentForm.reference}
                  onChange={(e) => setPaymentForm(prev => ({ ...prev, reference: e.target.value }))}
                  placeholder="Reference number"
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label>Notes</Label>
              <textarea
                value={paymentForm.notes}
                onChange={(e) => setPaymentForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Payment notes..."
                className="w-full mt-1 border rounded-md p-2 h-16"
              />
            </div>

            {/* Auto-allocate checkbox */}
            {financialData && financialData.invoices.filter(inv => inv.outstanding > 0).length > 0 && (
              <div className="border-t pt-4">
                <div className="flex items-center space-x-2 mb-4">
                  <Checkbox
                    id="auto_allocate"
                    checked={paymentForm.auto_allocate}
                    onCheckedChange={(checked) => {
                      setPaymentForm(prev => ({
                        ...prev,
                        auto_allocate: checked,
                        allocations: checked ? [] : prev.allocations  // Clear manual allocations if auto-allocating
                      }));
                    }}
                  />
                  <Label htmlFor="auto_allocate" className="text-sm font-medium">
                    Auto-allocate to oldest invoices first (FIFO)
                  </Label>
                </div>
                
                {paymentForm.auto_allocate ? (
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                    <p className="text-sm text-blue-700">
                      <Check className="h-4 w-4 inline mr-1" />
                      Payment will be automatically allocated to the oldest outstanding invoices first.
                    </p>
                  </div>
                ) : (
                  <>
                    <Label className="text-base font-semibold">Manual Allocation (Optional)</Label>
                    <p className="text-sm text-gray-500 mb-3">
                      Manually allocate this payment to specific invoices, or leave empty to allocate later.
                    </p>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Invoice #</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead className="text-right">Outstanding</TableHead>
                          <TableHead className="text-right">Allocate</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {financialData.invoices.filter(inv => inv.outstanding > 0).map((invoice, idx) => {
                          const allocation = paymentForm.allocations.find(a => a.grv_id === invoice.grv_id);
                          return (
                            <TableRow key={idx}>
                              <TableCell className="font-mono">{invoice.invoice_number}</TableCell>
                              <TableCell>{invoice.received_date}</TableCell>
                              <TableCell className="text-right">R {invoice.outstanding.toFixed(2)}</TableCell>
                              <TableCell className="text-right">
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={allocation?.amount || ""}
                                  onChange={(e) => handleAllocationChange(
                                    financialData.invoices.findIndex(i => i.grv_id === invoice.grv_id),
                                    e.target.value
                                  )}
                                  placeholder="0.00"
                                  className="w-28 text-right ml-auto"
                                  max={invoice.outstanding}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                    {paymentForm.allocations.length > 0 && (
                      <div className="mt-2 text-sm text-right">
                        Total Allocated: <span className="font-semibold">
                          R {paymentForm.allocations.reduce((sum, a) => sum + a.amount, 0).toFixed(2)}
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowPaymentModal(false)}>
              Cancel
            </Button>
            <Button onClick={() => handleSavePayment(false)} disabled={savingPayment} className="bg-green-600 hover:bg-green-700">
              {savingPayment ? "Saving..." : "Record Payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Overpayment Confirmation Dialog */}
      <Dialog open={showOverpayConfirm} onOpenChange={setShowOverpayConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              Confirm Overpayment
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800 mb-3">
                You are about to pay <strong>R {parseFloat(paymentForm.amount || 0).toFixed(2)}</strong> to {historySupplier?.name}.
              </p>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Available to Pay:</span>
                  <span className="font-semibold text-green-700">R {availableToPay.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Payment Amount:</span>
                  <span className="font-semibold">R {parseFloat(paymentForm.amount || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between border-t pt-2 mt-2">
                  <span className="text-amber-700 font-medium">Overpayment:</span>
                  <span className="font-bold text-amber-700">
                    R {(parseFloat(paymentForm.amount || 0) - availableToPay).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
            
            <p className="text-sm text-gray-600">
              This payment exceeds the amount available based on cost of goods sold. 
              Are you sure you want to proceed?
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOverpayConfirm(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => handleSavePayment(true)} 
              disabled={savingPayment}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {savingPayment ? "Saving..." : "Confirm & Pay"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Allocation Modal */}
      <Dialog open={showAllocateModal} onOpenChange={setShowAllocateModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CircleDollarSign className="h-5 w-5" />
              Allocate Payment to Invoice
            </DialogTitle>
          </DialogHeader>

          {selectedInvoice && (
            <div className="space-y-4 mt-4">
              {/* Invoice Details */}
              <div className="bg-gray-50 rounded-md p-4">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="text-gray-500">Invoice #:</div>
                  <div className="font-mono font-medium">{selectedInvoice.invoice_number}</div>
                  <div className="text-gray-500">GRV #:</div>
                  <div className="font-mono text-xs">{selectedInvoice.grv_number}</div>
                  <div className="text-gray-500">Invoice Total:</div>
                  <div>R {selectedInvoice.invoice_total.toFixed(2)}</div>
                  <div className="text-gray-500">Already Paid:</div>
                  <div className="text-green-600">R {selectedInvoice.amount_paid.toFixed(2)}</div>
                  <div className="text-gray-500 font-medium">Outstanding:</div>
                  <div className="font-semibold text-red-600">R {selectedInvoice.outstanding.toFixed(2)}</div>
                </div>
              </div>
              
              {/* Available Funds */}
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <div className="flex justify-between items-center">
                  <span className="text-amber-800 text-sm">Available Unallocated Funds:</span>
                  <span className="font-semibold text-amber-800">R {unallocatedFunds.toFixed(2)}</span>
                </div>
              </div>

              {/* Allocation Amount */}
              <div>
                <Label>Amount to Allocate (R)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={allocateAmount}
                  onChange={(e) => setAllocateAmount(e.target.value)}
                  placeholder="0.00"
                  className="mt-1"
                  max={Math.min(unallocatedFunds, selectedInvoice.outstanding)}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Max: R {Math.min(unallocatedFunds, selectedInvoice.outstanding).toFixed(2)}
                </p>
              </div>
            </div>
          )}

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowAllocateModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveAllocation} 
              disabled={savingAllocation}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {savingAllocation ? "Allocating..." : "Allocate Payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Journal Entry Modal */}
      <Dialog open={showJournalModal} onOpenChange={setShowJournalModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              New Journal Entry - {historySupplier?.name}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <Label>Entry Type</Label>
              <Select 
                value={journalForm.journal_type} 
                onValueChange={(v) => setJournalForm(prev => ({ ...prev, journal_type: v }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PAYMENT">Payment</SelectItem>
                  <SelectItem value="DISCOUNT">Discount Received</SelectItem>
                  <SelectItem value="CREDIT">Credit Note</SelectItem>
                  <SelectItem value="DEBIT">Debit Adjustment</SelectItem>
                  <SelectItem value="ADJUSTMENT">General Adjustment</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                {journalForm.journal_type === "PAYMENT" && "Record a payment made to this supplier"}
                {journalForm.journal_type === "DISCOUNT" && "Supplier discount that reduces the balance"}
                {journalForm.journal_type === "CREDIT" && "Credit received from supplier"}
                {journalForm.journal_type === "DEBIT" && "Additional charge or debit adjustment"}
                {journalForm.journal_type === "ADJUSTMENT" && "General balance adjustment"}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={journalForm.date}
                  onChange={(e) => setJournalForm(prev => ({ ...prev, date: e.target.value }))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Amount (R) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={journalForm.amount}
                  onChange={(e) => setJournalForm(prev => ({ ...prev, amount: e.target.value }))}
                  placeholder="0.00"
                  className="mt-1"
                />
              </div>
            </div>

            {journalForm.journal_type === "PAYMENT" && (
              <div>
                <Label>Payment Method</Label>
                <Select 
                  value={journalForm.payment_method} 
                  onValueChange={(v) => setJournalForm(prev => ({ ...prev, payment_method: v }))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="EFT">EFT</SelectItem>
                    <SelectItem value="Cash">Cash</SelectItem>
                    <SelectItem value="Cheque">Cheque</SelectItem>
                    <SelectItem value="Card">Card</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label>Reference</Label>
              <Input
                value={journalForm.reference}
                onChange={(e) => setJournalForm(prev => ({ ...prev, reference: e.target.value }))}
                placeholder="Bank reference, cheque number, etc."
                className="mt-1"
              />
            </div>

            <div>
              <Label>Description *</Label>
              <textarea
                value={journalForm.description}
                onChange={(e) => setJournalForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe this entry..."
                className="w-full mt-1 border rounded-md p-2 h-20"
              />
            </div>

            {/* Preview effect on balance */}
            {journalForm.amount && (
              <Card className={
                ["PAYMENT", "CREDIT", "DISCOUNT"].includes(journalForm.journal_type) 
                  ? "bg-green-50 border-green-200" 
                  : "bg-red-50 border-red-200"
              }>
                <CardContent className="py-3">
                  <div className="flex items-center gap-2">
                    {["PAYMENT", "CREDIT", "DISCOUNT"].includes(journalForm.journal_type) ? (
                      <>
                        <Check className="h-4 w-4 text-green-600" />
                        <span className="text-sm text-green-700">
                          This will <strong>reduce</strong> the balance by R {parseFloat(journalForm.amount || 0).toFixed(2)}
                        </span>
                      </>
                    ) : (
                      <>
                        <AlertTriangle className="h-4 w-4 text-red-600" />
                        <span className="text-sm text-red-700">
                          This will <strong>increase</strong> the balance by R {parseFloat(journalForm.amount || 0).toFixed(2)}
                        </span>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowJournalModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveJournal}
              disabled={savingJournal}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {savingJournal ? "Saving..." : "Save Entry"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* NEW: Supplier Detail Modal (Profile, Transactions, Journal, Reconcile) */}
      <SupplierDetailModal
        open={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedSupplier(null);
        }}
        supplier={selectedSupplier}
        onUpdate={fetchSuppliers}
      />

      {/* Data Cleanup Modal */}
      <SupplierDataCleanup
        open={showCleanupModal}
        onClose={() => setShowCleanupModal(false)}
      />

      {/* Find Duplicates Modal */}
      <DuplicateSuppliersDialog
        open={showDuplicatesModal}
        onClose={() => setShowDuplicatesModal(false)}
        onDeleted={() => { fetchSuppliers(); }}
      />
    </div>
  );
};

export default Suppliers;
